import React, { useEffect } from "react";
import { inject, observer } from "mobx-react";
import { useIntl } from "react-intl";
import AiChatStore from "common/broadcast/_stores/ai-chat/AiChatStore";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindText from "library/components/_tailwind/text";
import TailwindSpinner from "library/components/_tailwind/spinner";
import CustomIcon from "library/components/_tailwind/icon/icons";

const fadeInStyle = `
@keyframes aiCardFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

type Props = {
  aiChatStore?: AiChatStore;
  layoutStore?: LayoutStore;
};

const AiChatRecommendations: React.ComponentType<Props> = ({
  aiChatStore,
  layoutStore,
}) => {
  const intl = useIntl();
  const isEnabled = aiChatStore?.isEffectivelyEnabled;

  useEffect(() => {
    if (isEnabled) {
      aiChatStore?.setupReaction();
    }
    return () => {
      aiChatStore?.resetStore();
    };
  }, [isEnabled]);

  if (!aiChatStore?.isUiVisible) return null;

  const {
    aiRecommendations,
    isDraftingReplies,
    effectivePosition,
  } = aiChatStore!;

  const { isMobileDevice } = layoutStore!;
  const isTop = effectivePosition === "top";

  if (aiRecommendations.length === 0 && !isDraftingReplies) return null;

  const cardStyle: React.CSSProperties = {
    minHeight: "44px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    animation: "aiCardFadeIn 0.3s ease-out",
  };

  return (
    <>
      <style>{fadeInStyle}</style>
      <div
        style={{
          position: "absolute",
          ...(isTop ? { top: 0 } : { bottom: 0 }),
          right: 0,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "8px",
          padding: "8px",
          pointerEvents: "none",
          maxWidth: isMobileDevice ? "85%" : "50%",
        }}>
        {aiRecommendations.map((rec) => (
          <TailwindFlex
            key={rec.id}
            flexDirection={"flex-row"}
            alignItems={"items-center"}
            backgroundColor={"bg-white"}
            borderRadius={"rounded-lg"}
            padding={["px-3", "py-2"]}
            cursor={"cursor-pointer"}
            gap={"gap-2"}
            style={{ ...cardStyle, pointerEvents: "auto" }}
            onClick={() => aiChatStore?.useRecommendation(rec.id)}>
            <TailwindBox
              as={"span"}
              flex={"flex-none"}
              display={"inline-flex"}
              alignItems={"items-center"}>
              <span
                style={{
                  fontSize: "1rem",
                  color: "var(--pink-color)",
                  display: "inline-flex",
                }}>
                <CustomIcon name={"auto_awesome"} />
              </span>
            </TailwindBox>
            <TailwindFlex
              flexDirection={"flex-col"}
              flexGrow={"flex-grow"}
              style={{ minWidth: 0 }}>
              <TailwindText
                fontSize={"text-sm"}
                fontWeight={"font-bold"}
                textColor={"text-gray-500"}
                whiteSpace={"whitespace-nowrap"}>
                {rec.isSystemTrigger
                  ? intl.formatMessage({
                      id: "broadcast.aiChat.toPublicChat",
                      defaultMessage: "To: Public Chat",
                    })
                  : rec.isWhisper
                    ? intl.formatMessage(
                        {
                          id: "broadcast.aiChat.recommendationToWhisper",
                          defaultMessage: "To: {username} (Whisper)",
                        },
                        {
                          username: rec.targetUsername,
                        }
                      )
                    : intl.formatMessage(
                        {
                          id: "broadcast.aiChat.recommendationTo",
                          defaultMessage: "To: {username}",
                        },
                        {
                          username: rec.targetUsername,
                        }
                      )}
              </TailwindText>
              <TailwindText
                fontSize={"text-base"}
                textColor={"text-gray-800"}>
                {rec.suggestedReply}
              </TailwindText>
            </TailwindFlex>
            <TailwindBox
              as={"span"}
              flex={"flex-none"}
              display={"inline-flex"}
              alignItems={"items-center"}
              justifyContent={"justify-center"}
              cursor={"cursor-pointer"}
              padding={["p-1"]}
              style={{ margin: "-8px", padding: "8px" }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                aiChatStore?.dismissRecommendation(rec.id);
              }}>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: "#9ca3af",
                  display: "inline-flex",
                }}>
                <CustomIcon name={"close"} />
              </span>
            </TailwindBox>
          </TailwindFlex>
        ))}
        {isDraftingReplies && aiRecommendations.length === 0 && (
          <TailwindFlex
            flexDirection={"flex-row"}
            alignItems={"items-center"}
            backgroundColor={"bg-white"}
            borderRadius={"rounded-lg"}
            padding={["px-3", "py-2"]}
            gap={"gap-2"}
            style={{ ...cardStyle, pointerEvents: "auto" }}>
            <TailwindBox
              as={"span"}
              flex={"flex-none"}
              display={"inline-flex"}
              alignItems={"items-center"}>
              <TailwindSpinner size={1} margin={["m-0"]} />
            </TailwindBox>
            <TailwindText
              fontSize={"text-sm"}
              textColor={"text-gray-400"}
              fontStyle={"italic"}>
              {intl.formatMessage({
                id: "broadcast.aiChat.drafting",
                defaultMessage: "AI is drafting replies...",
              })}
            </TailwindText>
          </TailwindFlex>
        )}
      </div>
    </>
  );
};

export default inject(
  "aiChatStore",
  "layoutStore"
)(observer(AiChatRecommendations));
